import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Modal, Button, Row, Spin } from 'antd';
import { MinusOutlined } from '@ant-design/icons';
import Loading from 'components/Loading';
import EditorTexto from 'components/EditorTexto';
import InputTextArea from 'components/InputTextArea';
import ButtonUpload from 'components/Button/ButtonUpload';
import ButtonIcon from 'components/Button/ButtonIcon';
import {
  removerArquivo,
  adicionarNovoArquivo,
  renderFrase,
  abrirArquivo,
  renderArquivos,
  getRotaFinalizar,
} from 'components/Modal/ModalJustificativa/Modal';
import { duckDadosDetalhar } from 'store/ducks/formulario';
import {
  fetchNotificationApp,
  fetchUpdateDadosMinimizados,
  fetchStatusModalJustificativa,
  fetchStatusLoading,
  fetchStatusModal,
} from 'store/fetchActions/fetchApp';
import { fetchDetalharFormularioPorId } from 'store/fetchActions/fetchFormulario';
import { fetchDownloadTodosArquivos } from 'store/fetchActions/fetchArquivo';
import { formatterJustificativaNull } from 'utils/formatter/justificativa';
import { changeInput } from 'utils/formUtils';
import { MSG_FORMULARIO_EDITADO, ALERTA, ENUM_MODAL, MSG_TOOLTIP_BAIXAR_TODOS_ARQUIVOS } from 'utils/consts';
import ButtonModalContribuinte from './ButtonContribuinte';
import ButtonUsuarioFrente from './ButtonUsuarioFrente';
import ButtonUsuarioSetorial from './ButtonUsuarioSetorial';
import * as S from './styles';

export const ModalGenericoJustificativa = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const modalAcessoNegado = useSelector(state => state.app.modalAcessoNegado);
  const thisModalState = useSelector(state => state.app.modalJustificativa);
  const dadosMinimizados = useSelector(state => state.app.dadosMinimizados);
  const requerimentoDetalhe = useSelector(state => state.formulario.dadosExibirDetalhar);

  const [loading, setLoading] = React.useState(false);
  const [arquivos, setArquivos] = React.useState([]);
  const [tamanhoTotalArquivos, setTamanhoTotalArquivos] = React.useState(0);

  const {
    control,
    setValue,
    getValues,
    setError,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: formatterJustificativaNull(dadosMinimizados),
  });

  React.useEffect(() => {
    //NÃO ABRIR ESSE MODAL SE O MODAL DE ACESSO NEGADO ESTIVER ABERTO
    if (modalAcessoNegado?.isOpen && thisModalState?.isOpen) {
      dispatch(fetchStatusModalJustificativa(false, ''));
    }
  }, [dispatch, modalAcessoNegado, thisModalState]);

  React.useEffect(() => {
    //CARREGAR ARQUIVOS DO FORMULARIO NO MODAL
    if (!thisModalState.isOpen && dadosMinimizados) {
      setArquivos(dadosMinimizados?.arquivos || requerimentoDetalhe?.arquivos);
    }
  }, [thisModalState, dadosMinimizados, requerimentoDetalhe]);

  React.useEffect(() => {
    if (!dadosMinimizados?.nameModal) {
      reset();
      setArquivos(arquivos.filter(arquivo => arquivo.nomeArquivo));
      setTamanhoTotalArquivos(0);
    }
    // eslint-disable-next-line
  }, [dadosMinimizados]);

  const onClose = rotaRedirect => {
    reset();
    if (rotaRedirect) history.push(rotaRedirect);
    setArquivos(arquivos.filter(arquivo => arquivo.nomeArquivo));
    setTamanhoTotalArquivos(0);
    dispatch(fetchUpdateDadosMinimizados({}));
    dispatch(fetchStatusModalJustificativa(false, ''));
    dispatch(fetchStatusModal(false, ''));
  };

  const getDadosJustificativa = () => {
    const dadosInputs = getValues();
    return {
      ...dadosMinimizados,
      justificativa: dadosInputs?.justificativa,
      arquivos: arquivos,
      nameModal: thisModalState.title,
    };
  };

  const onMinimize = () => {
    const dadosMinimizadosCompleto = getDadosJustificativa();

    dispatch(fetchUpdateDadosMinimizados(dadosMinimizadosCompleto));
    dispatch(fetchStatusModalJustificativa(false, ''));
  };

  const sucessoFinalizar = statusSelect => {
    const rotaRedirect = getRotaFinalizar(statusSelect);
    onClose(rotaRedirect);
    fetchNotificationApp(MSG_FORMULARIO_EDITADO, '', ALERTA.SUCESSO);
    setLoading(false);
    if (!rotaRedirect) {
      dispatch(fetchDetalharFormularioPorId(requerimentoDetalhe?.id));
    }
  };

  const sucessoFinalizarContribuinte = formularioAtualizado => {
    dispatch(fetchStatusModalJustificativa(false, ''));
    fetchNotificationApp(MSG_FORMULARIO_EDITADO, '', ALERTA.SUCESSO);
    dispatch(duckDadosDetalhar(formularioAtualizado));
    setLoading(false);
  };

  const handleChange = e => {
    changeInput(e, setValue, errors, setError);
  };

  const handleAdicionarNovoArquivo = file => {
    const { tamanhoTotal, novoArquivo, isValid } = adicionarNovoArquivo(file, tamanhoTotalArquivos);
    if (isValid) {
      setTamanhoTotalArquivos(tamanhoTotal);
      setArquivos([...arquivos, novoArquivo]);
    }
  };

  const handleRemoverArquivo = id => {
    const { tamanhoTotal, novosAquivos } = removerArquivo(id, arquivos, tamanhoTotalArquivos);
    setTamanhoTotalArquivos(tamanhoTotal);
    setArquivos(novosAquivos);
  };

  async function handleAbrirArquivo(arquivo) {
    dispatch(fetchStatusLoading(true));
    abrirArquivo(arquivo);
    setTimeout(function () {
      dispatch(fetchStatusLoading(false));
    }, 10000);
  }

  const renderButtons = () => {
    if (thisModalState.title === ENUM_MODAL.JUSTIFICATIVA.CONTRIBUINTE) {
      return (
        <ButtonModalContribuinte
          setExibirLoading={setLoading}
          sucessoFinalizar={sucessoFinalizarContribuinte}
          valueJustificativa={getValues()?.justificativa}
          arquivos={arquivos}
          setError={setError}
        />
      );
    }
    if (thisModalState.title === ENUM_MODAL.JUSTIFICATIVA.USUARIO_SETORIAL) {
      return (
        <ButtonUsuarioSetorial
          setExibirLoading={setLoading}
          sucessoFinalizar={sucessoFinalizar}
          valueJustificativa={getValues()?.justificativa}
          arquivos={arquivos}
          setError={setError}
          resetModal={reset}
          getDadosJustificativa={getDadosJustificativa}
        />
      );
    } else {
      return (
        <ButtonUsuarioFrente
          setExibirLoading={setLoading}
          sucessoFinalizar={sucessoFinalizar}
          valueJustificativa={getValues()?.justificativa}
          arquivos={arquivos}
          setError={setError}
          resetModal={reset}
          getDadosJustificativa={getDadosJustificativa}
        />
      );
    }
  };

  const renderInputContribuinte = () => {
    return (
      <InputTextArea
        label="* Justificativa"
        name="justificativa"
        errors={errors}
        placeholder={'Justificativa'}
        control={control}
        onChange={handleChange}
        maxLength={2000}
        rows={5}
      />
    );
  };

  const renderInputSefaz = () => {
    return (
      <EditorTexto
        name="justificativa"
        tooltipSwitchText="Visualizar Justificativa"
        errors={errors}
        data={getValues().justificativa}
        handleChange={handleChange}
      />
    );
  };

  return (
    <Modal
      title={thisModalState?.title}
      centered
      visible={thisModalState?.isOpen}
      onCancel={onMinimize}
      closeIcon={<MinusOutlined data-testid="Minimizar_Icon" />}
      width={900}
      closable={true}
      forceRender
      destroyOnClose
      footer={[
        <Button key="back" onClick={onClose}>
          Fechar
        </Button>,
      ]}
    >
      <Loading>
        <Spin tip="Carregando..." spinning={loading}>
          <div>
            <S.CarregarDadosLabel>
              {renderFrase(thisModalState.title)}
              <br />
              <br />
              Tamanho total da Justificativa <b>{`${watch('justificativa').length}/2000`}</b>
              <br />
              Tamanho total dos arquivos: <b>{tamanhoTotalArquivos} MB/10 MB</b>
            </S.CarregarDadosLabel>
            {thisModalState.title === ENUM_MODAL.JUSTIFICATIVA.CONTRIBUINTE
              ? renderInputContribuinte()
              : renderInputSefaz()}
            <Row>
              <ButtonUpload funcUpload={handleAdicionarNovoArquivo} />
              <ButtonIcon
                icon="Baixar"
                tooltipMessage={MSG_TOOLTIP_BAIXAR_TODOS_ARQUIVOS}
                clickAction={() => fetchDownloadTodosArquivos(requerimentoDetalhe?.id)}
                style={{ marginLeft: '10px' }}
                disabled={requerimentoDetalhe?.arquivos?.length === 0}
              />
            </Row>
            {renderArquivos(arquivos, handleAbrirArquivo, handleRemoverArquivo)}
            <Row>{renderButtons()}</Row>
          </div>
        </Spin>
      </Loading>
    </Modal>
  );
};

export default ModalGenericoJustificativa;
